// Worksheet Wizard Types - Based on @kgc/service-worksheet interfaces

// Re-use Partner from rental
export type { Partner } from '../rental/types';

/**
 * Munkalap státuszok
 */
export enum WorksheetStatus {
  FELVEVE = 'FELVEVE',
  FOLYAMATBAN = 'FOLYAMATBAN',
  VARHATO = 'VARHATO',
  FELFUGGESZTVE = 'FELFUGGESZTVE',
  KESZ = 'KESZ',
  SZAMLAZANDO = 'SZAMLAZANDO',
  LEZART = 'LEZART',
  TOROLVE = 'TOROLVE',
}

/**
 * Munkalap típusok
 */
export enum WorksheetType {
  FIZETOS = 'FIZETOS',
  GARANCIALIS = 'GARANCIALIS',
  BERLESI = 'BERLESI',
  KARBANTARTAS = 'KARBANTARTAS',
  SZERZODESES = 'SZERZODESES',
}

/**
 * Javítás típusok (wizard repair type selection)
 */
export type RepairType = 'FIZETOS' | 'GARANCIALIS' | 'SZERZODESES';

/**
 * Prioritások
 */
export enum WorksheetPriority {
  NORMAL = 'NORMAL',
  SURGOS = 'SURGOS',
  FELARAS = 'FELARAS',
  GARANCIALIS = 'GARANCIALIS',
  FRANCHISE = 'FRANCHISE',
}

/**
 * Termék/Gép a szervizhez
 */
export interface Product {
  id: string;
  name: string;
  brand?: string;
  model?: string;
  serialNumber?: string;
  category: ProductCategory;
  warrantyExpiry?: Date;
  purchaseDate?: Date;
}

export enum ProductCategory {
  POWER_TOOL = 'POWER_TOOL',
  GARDEN = 'GARDEN',
  CONSTRUCTION = 'CONSTRUCTION',
  CLEANING = 'CLEANING',
  OTHER = 'OTHER',
}

/**
 * Alkatrész
 */
export interface Part {
  id: string;
  name: string;
  sku: string;
  unitPrice: number;
  inStock: number;
  category: string;
}

/**
 * Munkadíj norma (Makita style)
 */
export interface LaborNorm {
  id: string;
  code: string;
  description: string;
  minutes: number;
  hourlyRate: number;
  calculatedPrice: number;
}

/**
 * Munkalap tétel (alkatrész vagy munkadíj)
 */
export interface WorksheetItem {
  id: string;
  type: 'PART' | 'LABOR' | 'OTHER';
  description: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
  netAmount: number;
  grossAmount: number;
  partId?: string;
  normId?: string;
}

/**
 * Wizard állapot
 */
export interface WorksheetWizardState {
  step: number;
  // Partner
  partnerId: string | null;
  partnerName: string;
  // Gép
  product: Product | null;
  deviceName: string;
  serialNumber: string;
  // Probléma
  worksheetType: WorksheetType;
  priority: WorksheetPriority;
  faultDescription: string;
  costLimit: number | null;
  // Diagnosztika
  diagnosis: string;
  workPerformed: string;
  items: WorksheetItem[];
  // Összesítés
  estimatedCompletionDate: Date | null;
  internalNote: string;
}

// Label keys (i18n)
export const STATUS_LABEL_KEYS: Record<WorksheetStatus, string> = {
  [WorksheetStatus.FELVEVE]: 'worksheet.status.felveve',
  [WorksheetStatus.FOLYAMATBAN]: 'worksheet.status.folyamatban',
  [WorksheetStatus.VARHATO]: 'worksheet.status.varhato',
  [WorksheetStatus.FELFUGGESZTVE]: 'worksheet.status.felfuggesztve',
  [WorksheetStatus.KESZ]: 'worksheet.status.kesz',
  [WorksheetStatus.SZAMLAZANDO]: 'worksheet.status.szamlazando',
  [WorksheetStatus.LEZART]: 'worksheet.status.lezart',
  [WorksheetStatus.TOROLVE]: 'worksheet.status.torolve',
};

export const TYPE_LABEL_KEYS: Record<WorksheetType, string> = {
  [WorksheetType.FIZETOS]: 'worksheet.type.fizetos',
  [WorksheetType.GARANCIALIS]: 'worksheet.type.garancialis',
  [WorksheetType.BERLESI]: 'worksheet.type.berlesi',
  [WorksheetType.KARBANTARTAS]: 'worksheet.type.karbantartas',
  [WorksheetType.SZERZODESES]: 'worksheet.type.szerzodeses',
};

export const PRIORITY_LABEL_KEYS: Record<WorksheetPriority, string> = {
  [WorksheetPriority.NORMAL]: 'worksheet.priority.normal',
  [WorksheetPriority.SURGOS]: 'worksheet.priority.surgos',
  [WorksheetPriority.FELARAS]: 'worksheet.priority.surcharge',
  [WorksheetPriority.GARANCIALIS]: 'worksheet.priority.garancialis',
  [WorksheetPriority.FRANCHISE]: 'worksheet.priority.franchise',
};

export const CATEGORY_LABEL_KEYS: Record<ProductCategory, string> = {
  [ProductCategory.POWER_TOOL]: 'worksheet.category.powerTool',
  [ProductCategory.GARDEN]: 'worksheet.category.garden',
  [ProductCategory.CONSTRUCTION]: 'worksheet.category.construction',
  [ProductCategory.CLEANING]: 'worksheet.category.cleaning',
  [ProductCategory.OTHER]: 'worksheet.category.other',
};

/**
 * Teljes munkalap rekord (wizard state-ből generált)
 */
export interface Worksheet {
  id: string;
  worksheetNumber: string;
  // Partner
  partnerId: string;
  partnerName: string;
  partnerPhone?: string;
  partnerEmail?: string;
  // Gép/Termék
  product: Product;
  // Munka részletek
  worksheetType: WorksheetType;
  priority: WorksheetPriority;
  status: WorksheetStatus;
  faultDescription: string;
  diagnosis: string;
  workPerformed: string;
  // Tételek
  items: WorksheetItem[];
  // Pénzügyi
  costLimit: number | null;
  netTotal: number;
  vatTotal: number;
  grossTotal: number;
  depositPaid: number;
  // Dátumok
  createdAt: string;
  updatedAt: string;
  estimatedCompletionDate: string | null;
  completedAt: string | null;
  invoicedAt: string | null;
  // Személyek
  createdBy: string;
  assignedTo: string | null;
  // Megjegyzések
  internalNote: string;
  customerNote: string;
  // Történet
  history: WorksheetHistoryEntry[];
}

/**
 * Munkalap történet bejegyzés
 */
export interface WorksheetHistoryEntry {
  id: string;
  timestamp: string;
  action: WorksheetHistoryAction;
  description: string;
  userId: string;
  userName: string;
  oldValue?: string;
  newValue?: string;
}

export enum WorksheetHistoryAction {
  CREATED = 'CREATED',
  STATUS_CHANGED = 'STATUS_CHANGED',
  ITEM_ADDED = 'ITEM_ADDED',
  ITEM_REMOVED = 'ITEM_REMOVED',
  ASSIGNED = 'ASSIGNED',
  NOTE_ADDED = 'NOTE_ADDED',
  INVOICED = 'INVOICED',
  COMPLETED = 'COMPLETED',
}

export const HISTORY_ACTION_LABEL_KEYS: Record<WorksheetHistoryAction, string> = {
  [WorksheetHistoryAction.CREATED]: 'worksheet.history.created',
  [WorksheetHistoryAction.STATUS_CHANGED]: 'worksheet.history.statusChanged',
  [WorksheetHistoryAction.ITEM_ADDED]: 'worksheet.history.itemAdded',
  [WorksheetHistoryAction.ITEM_REMOVED]: 'worksheet.history.itemRemoved',
  [WorksheetHistoryAction.ASSIGNED]: 'worksheet.history.assigned',
  [WorksheetHistoryAction.NOTE_ADDED]: 'worksheet.history.noteAdded',
  [WorksheetHistoryAction.INVOICED]: 'worksheet.history.invoiced',
  [WorksheetHistoryAction.COMPLETED]: 'worksheet.history.completed',
};
